import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import DropdownButton from '../../basics/DropdownButton';
import ExportPdfButton from './ExportPdfButton';
import ExportCsvButton from './ExportCsvButton';
import ExportShpButton, {
  canExportShpBoundary,
  canExportShpLandmarkLines,
} from './ExportShpButton';
import ExportSectionKmlButton from './ExportSectionKmlButton';
import ExportSectionJsonButton from './ExportSectionJsonButton';

const pointOrderOptions = [
  { name: 'lat,lng', order: [1, 0] },
  { name: 'lng,lat', order: [0, 1] },
];

const csvExportOptions = [
  { name: 'Single CSV', value: 'single' },
  { name: 'Multiple CSVs (zipped)', value: 'multiple' },
];

const filetypeOptions = ['CSV', 'JSON', 'KML', 'PDF', 'SHP'].map((name) => ({
  name,
}));
const granularityOptions = ['points', 'rows', 'sections'].map((value) => ({
  name: _.capitalize(value),
  value,
}));
const jsonKmlOptions = ['points', 'rows', 'boundary'].map((value) => ({
  name: _.capitalize(value),
  value,
}));
const shpOptions = [
  { name: 'Points', value: 'points' },
  { name: 'Rows', value: 'rows' },
  {
    name: 'Boundary',
    value: 'boundary',
    disabledTooltip: `Boundary can't be exported for line sections`,
    filter: canExportShpBoundary,
  },
  // The landmark lines option is used to generate a shapefile with
  // lines perpendicular to the rows that go through all the points.
  // This option is only available for grid sections with no row overrides.
  // Some tractors use rows, landmark lines, and the field boundary to
  // determine the locations of the points.
  {
    name: 'Landmark lines',
    value: 'landmark lines',
    disabledTooltip:
      'Landmark lines can only be exported for grid sections with equal in-row spacing',
    filter: canExportShpLandmarkLines,
  },
];

const AlignedDropdown = styled(DropdownButton)`
  margin-left: -0.5rem;
`;

const ExportButtons = ({
  sections,
  cropData,
  title,
  subtitle,
  ...buttonProps
}) => {
  const [pointOrder, setPointOrder] = useState(pointOrderOptions[0]);
  const [granularity, setGranularity] = useState(granularityOptions[0]);
  const [filetype, setFiletype] = useState(filetypeOptions[0]);
  const [jsonKmlOption, setJsonKmlOption] = useState(jsonKmlOptions[0]);
  const [shpOption, setShpOption] = useState(shpOptions[0]);
  const [singleCSVOption, setSingleCSVOption] = useState(csvExportOptions[0]);

  // A map from shapefile export options to the filtered sections that
  // match that option.
  const shpSectionsByOption = useMemo(() => {
    if (!sections || filetype.name !== 'SHP') return {};
    return shpOptions.reduce((acc, option) => {
      const { value, filter } = option;
      acc[value] = filter ? sections.filter(filter) : sections;
      return acc;
    }, {});
  }, [sections, filetype]);

  // Disable options that don't have at least one matching section.
  const validShpOptions = useMemo(() => {
    if (!sections || filetype.name !== 'SHP') return [];
    return shpOptions.map((option) => {
      const disabled = (shpSectionsByOption[option.value] || []).length === 0;
      return {
        ...option,
        disabled,
        tooltip: disabled ? option.disabledTooltip : undefined,
      };
    });
  }, [filetype.name, sections, shpSectionsByOption]);

  useEffect(() => {
    setShpOption(validShpOptions.find(({ disabled }) => !disabled) || {});
  }, [sections, validShpOptions]);

  return (
    <>
      <div className="d-flex align-items-center mb-1">
        <AlignedDropdown
          color="light"
          options={filetypeOptions}
          selectedOption={filetype.name}
          setOption={setFiletype}
          showOverflow
        />
        {filetype.name === 'CSV' && (
          <>
            <DropdownButton
              className="ml-1"
              color="light"
              options={granularityOptions}
              selectedOption={granularity.name}
              setOption={setGranularity}
              showOverflow
            />
            {granularity.value === 'points' && (
              <DropdownButton
                className="ml-1"
                color="light"
                options={pointOrderOptions}
                selectedOption={pointOrder.name}
                setOption={setPointOrder}
                showOverflow
              />
            )}
          </>
        )}
        {filetype.name === 'JSON' && (
          <DropdownButton
            className="ml-1"
            color="light"
            options={jsonKmlOptions}
            selectedOption={jsonKmlOption.name}
            setOption={setJsonKmlOption}
            showOverflow
          />
        )}
        {filetype.name === 'KML' && (
          <DropdownButton
            className="ml-1"
            color="light"
            options={jsonKmlOptions}
            selectedOption={jsonKmlOption.name}
            setOption={setJsonKmlOption}
            showOverflow
          />
        )}
        {filetype.name === 'SHP' && (
          <DropdownButton
            className="ml-1"
            color="light"
            options={validShpOptions}
            selectedOption={shpOption.name}
            setOption={setShpOption}
            showOverflow
          />
        )}
      </div>
      {/* If user has selected CSV & points and there are multiple sections to be exported, let them check if they want 1 CSV or multiple CSVs zipped (1 per section). */}
      {filetype.name === 'CSV' &&
        granularity.value === 'points' &&
        sections.length > 1 && (
          <div className="d-flex mb-2">
            <AlignedDropdown
              color="light"
              options={csvExportOptions}
              selectedOption={singleCSVOption.name}
              setOption={setSingleCSVOption}
              showOverflow
            />
          </div>
        )}
      {filetype.name === 'CSV' && (
        <ExportCsvButton
          sections={sections}
          pointOrder={pointOrder}
          granularity={granularity.value}
          title={`${granularity.name} - ${title}`}
          shouldUseSingleCSV={singleCSVOption.value === 'single'}
          {...buttonProps}
        />
      )}
      {filetype.name === 'JSON' && (
        <ExportSectionJsonButton
          sections={sections}
          granularity={jsonKmlOption.value}
          title={jsonKmlOption.name}
          {...buttonProps}
        />
      )}
      {filetype.name === 'KML' && (
        <ExportSectionKmlButton
          sections={sections}
          granularity={jsonKmlOption.value}
          title={jsonKmlOption.name}
          {...buttonProps}
        />
      )}
      {filetype.name === 'SHP' && (
        <ExportShpButton
          sections={shpSectionsByOption[shpOption.value] || sections}
          shpOption={shpOption.value}
          {...buttonProps}
        />
      )}
      {filetype.name === 'PDF' && (
        <ExportPdfButton
          cropData={cropData}
          title={title}
          subtitle={subtitle}
          sections={sections}
          {...buttonProps}
        />
      )}
    </>
  );
};

export default ExportButtons;
